<script setup lang="ts">
// Libraries import
import { computed, markRaw, ref } from "vue";
import { useI18n } from "vue-i18n";

// Helpers import
import { formattedAddress } from "@/helpers/address";

import type { IAddress } from '@/types/address';

// Data et type import
import { BadgeColor, BadgeSize } from "@/dsComponents/Badge";
import Badge from "@/dsComponents/Badge/Badge.vue";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import AddressAutocomplete from "@/dsComponents/inputs/AddressAutocomplete.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import TagTitle from "@/dsComponents/modals/TagTitle.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

// Component import
import MapView from "@/components/MapView.vue";

interface MissionLocationModal {
    isModalOpen: boolean;
    missionLocations: IAddress[];
}

const columns = [
    { field: "isLocated", width: "22px", header: "" },
    { field: "name", width: "220px", header: "" },
    { field: "formattedAddress", width: "auto", header: "" },
];

const emit = defineEmits<{
    (e: "close"): void;
    (e: "address", address: IAddress): void;
}>();

const isLocated = (address?: IAddress) => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    if (!address || !address.location) return false;
    return address.location.length === 2;
};

const formatData = (addresses: IAddress[]) => {
    return addresses.map((address) => {
        return {
            content: {
                isLocated: {
                    customElement: {
                        component: markRaw(Badge),
                        props: {
                            size: BadgeSize.sm,
                            icon: "world-pin",
                            iconColor: isLocated(address) ? BadgeColor.green : BadgeColor.red,
                        },
                    },
                },
                name: { text: { value: address.name || "" } },
                formattedAddress: { text: { value: formattedAddress(address) } },
            },
            editMode: false,
        };
    });
};
const tableData = computed(() => formatData(props.missionLocations));

const addressInput = ref("");

const newAddress = ref<IAddress>();

const handleSelectAddress = (address: IAddress) => {
    newAddress.value = address;
    focused.value = false;
};

const handleSave = () => {
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    emit("address", newAddress.value!);
};

const handleSavedLocation = (rowIndex: number) => {
    addressInput.value = tableData.value[rowIndex].content.formattedAddress.text.value;
    newAddress.value = props.missionLocations[rowIndex];

    focused.value = !isLocated(props.missionLocations[rowIndex]);
};

const mapAddress = computed(() => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    if (!newAddress.value || newAddress.value.location?.length !== 2) return [];

    return [
        {
            id: 0,
            longitude: newAddress.value.location[0],
            latitude: newAddress.value.location[1],
            name: newAddress.value.name || "",
        },
    ];
});

const props = defineProps<MissionLocationModal>();
const { t } = useI18n();
const focused = ref(props.isModalOpen);

const handleClose = () => {
    addressInput.value = "";
    emit("close");
};

const handleInputChange = (value: string) => {
    focused.value = true;
    addressInput.value = value;
};
</script>
<template>
    <Modal position="top" :open="isModalOpen" @close="handleClose" width="720px" close action center-header :zIndexLevel="1">
        <template #header>
            <TagTitle label="Lieu de mission" />
        </template>
        <div class="mission-location-modal">
            <div class="modal-form">
                <AddressAutocomplete
                    :label="''"
                    :placeholder="t('page.order.location.searchPlaceholder')"
                    :modelValue="addressInput"
                    @update:model-value="handleInputChange"
                    @address="handleSelectAddress"
                    class="span-12"
                    width="100%"
                    :required="true"
                    alt
                    variant="transparent"
                    :focused="focused"
                />
                <div class="table-container">
                    <div class="table-title">Lieux de mission enregistrés</div>
                    <div class="tableAlt-content">
                        <TableAlt :columns="columns" :rows="tableData" rowHover rowClick @rowClick="handleSavedLocation" />
                    </div>
                </div>
            </div>
            <div v-if="isModalOpen" class="map">
                <MapView :rows="mapAddress" :zoom="9" />
            </div>
        </div>
        <template #footer>
            <div class="misison-location-modal-footer">
                <ButtonAlt type="neutral" variant="ghost" @click="handleClose" :loading="false">
                    {{ t("global.actions.cancel") }}
                </ButtonAlt>
                <ButtonAlt
                    type="neutral"
                    variant="filled"
                    @click="handleSave()"
                    :disabled="!addressInput || !isLocated(newAddress)"
                    :label="t('global.actions.save')"
                ></ButtonAlt>
            </div>
        </template>
    </Modal>
</template>
<style lang="scss" scoped>
.mission-location-modal {
    display: flex;
    flex-direction: column;
    gap: 16px;
    .modal-form {
        display: flex;
        flex-direction: column;
        gap: 24px;

        .table-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }
        .table-title {
            color: $text-sub-600;
            @include font-size-12;
        }

        .tableAlt-content {
            overflow-y: scroll;

            max-height: 150px;
        }
    }

    .map {
        height: 280px;
    }
}

.misison-location-modal-footer {
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: flex-end;
}
</style>
